import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  IconButton,
} from '@mui/material'
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material'

import { useReceivingStore } from 'store/receiving'
import type {
  ProductType,
  OwnershipType,
  UndocumentedItem,
} from 'store/receiving'

import { WorkflowScanProvider } from './WorkflowScanProvider'

import { useSnackbar } from 'notistack'
import useSearchProduct from 'lib/services/api/product-service/searchProduct'

import styles from './ScanStep.module.scss'
import type { ScanStepProps } from './ScanStep.types'

const ScanStep: React.FC<ScanStepProps> = ({ onNext, onBack }) => {
  const ownershipType = useReceivingStore((s) => s.ownershipType)
  const productType = useReceivingStore((s) => s.productType)
  const undocumentedItems = useReceivingStore((s) => s.undocumentedItems)
  const addUndocumentedItem = useReceivingStore((s) => s.addUndocumentedItem)
  const updateItemQuantity = useReceivingStore((s) => s.updateItemQuantity)

  const { enqueueSnackbar } = useSnackbar()

  const [isProcessingScan, setIsProcessingScan] = useState(false)
  const [scannedCode, setScannedCode] = useState<string | null>(null)
  const [editingQuantities, setEditingQuantities] = useState<
    Record<string, string>
  >({})
  const SCAN_COOLDOWN_MS = 800

  const handleScan = (barcode: string) => {
    if (!barcode || isProcessingScan) {
      return
    }

    setIsProcessingScan(true)
    setScannedCode(barcode)
  }

  const { data: searchProductResult, loading: isLoadingSearch } =
    useSearchProduct({
      udi: scannedCode || '',
      enabled: !!scannedCode,
    })

  useEffect(() => {
    if (!scannedCode) return
    if (isLoadingSearch) return

    const finishScan = () => {
      setScannedCode(null)
      setTimeout(() => setIsProcessingScan(false), SCAN_COOLDOWN_MS)
    }

    const existingItem = undocumentedItems.find(
      (item: UndocumentedItem) => item.udi === scannedCode
    )
    if (existingItem) {
      addUndocumentedItem({
        udi: scannedCode,
        deviceDescription: existingItem.deviceDescription,
        companyName: existingItem.companyName,
        assetType: productType as ProductType,
        ownershipType: ownershipType as OwnershipType,
        quantity: 1,
      })
      enqueueSnackbar(
        `Quantity increased for: ${existingItem.deviceDescription}`,
        {
          variant: 'success',
          autoHideDuration: 2000,
        }
      )
      finishScan()
      return
    }

    const result = searchProductResult?.searchProductsByUdi
    if (result && Array.isArray(result.assets)) {
      if (result.assets.length > 0) {
        const asset = result.assets[0]
        const name = asset.deviceDescription || asset.gmdnPTName || `Product`
        addUndocumentedItem({
          udi: scannedCode,
          deviceDescription: name,
          companyName: asset.company?.name || 'Unknown Company',
          assetType: productType as ProductType,
          ownershipType: ownershipType as OwnershipType,
          quantity: 1,
          catalogNumber: asset.catalogNumber,
          deviceId: asset.deviceId,
          versionModelNumber: asset.versionModelNumber,
          deviceCount: asset.deviceCount,
          lotBatch: result.productionIdentifier?.lotBatch,
          serialNumber: result.productionIdentifier?.serialNumber,
          expirationDate: result.productionIdentifier?.expirationDate
            ? new Date(result.productionIdentifier.expirationDate).toISOString()
            : null,
          packageIntegrity: productType === 'Biologic' ? true : undefined,
          temperature: productType === 'Biologic' ? true : undefined,
        })
        enqueueSnackbar(`Added: ${name}`, {
          variant: 'success',
          autoHideDuration: 2000,
        })
      } else {
        enqueueSnackbar('No product found. Please recapture the label.', {
          variant: 'warning',
          autoHideDuration: 2500,
        })
      }
      finishScan()
    }
  }, [
    scannedCode,
    isLoadingSearch,
    searchProductResult,
    undocumentedItems,
    addUndocumentedItem,
    enqueueSnackbar,
    productType,
    ownershipType,
  ])

  const handleQuantityInputChange = (itemId: string, value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setEditingQuantities((prev) => ({ ...prev, [itemId]: value }))
    }
  }

  const handleQuantityInputCommit = (itemId: string) => {
    const raw = editingQuantities[itemId]
    let nextValue = raw ? parseInt(raw, 10) : NaN
    if (!Number.isFinite(nextValue) || nextValue < 1) {
      nextValue = 1
    }
    setEditingQuantities((prev) => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
    updateItemQuantity(itemId, nextValue)
  }

  const incrementQuantity = (itemId: string, currentQuantity: number) => {
    setEditingQuantities((prev) => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
    updateItemQuantity(itemId, currentQuantity + 1)
  }

  const decrementQuantity = (itemId: string, currentQuantity: number) => {
    if (currentQuantity > 1) {
      setEditingQuantities((prev) => {
        const next = { ...prev }
        delete next[itemId]
        return next
      })
      updateItemQuantity(itemId, currentQuantity - 1)
    }
  }

  const handleNextStep = () => {
    if (undocumentedItems.length > 0) {
      onNext()
    } else {
      enqueueSnackbar('Please capture at least one item before proceeding', {
        variant: 'warning',
        autoHideDuration: 3000,
      })
    }
  }

  const pluralize = (count: number, singular: string, plural?: string) =>
    `${count} ${count === 1 ? singular : plural || `${singular}s`}`

  const totalQty = undocumentedItems.reduce(
    (sum: number, item: UndocumentedItem) => sum + item.quantity,
    0
  )

  return (
    <Box className={styles.scanStep}>
      <Box className={styles.scanHeader}>
        <Box className={styles.headerRow}>
          <Typography variant="h5" className={styles.title}>
            Receiving {ownershipType?.toLowerCase()} items - Capture{' '}
            {productType?.toLowerCase()} items
          </Typography>
          <Chip
            label={`${pluralize(
              undocumentedItems.length,
              'item'
            )} captured (${totalQty} total qty)`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      <Box className={styles.scanContent}>
        <Box className={styles.scannerSection}>
          <Box className={styles.scannerContainer}>
            <WorkflowScanProvider
              onScan={handleScan}
              canScan={!isProcessingScan}
            />
          </Box>
        </Box>

        <Box className={styles.itemsSection}>
          <Box className={styles.itemsContainer}>
            {undocumentedItems.length === 0 ? (
              <Box className={styles.emptyState}>
                <Typography variant="body2" color="text.secondary">
                  No items captured yet
                </Typography>
              </Box>
            ) : (
              <Box className={styles.itemsList}>
                {undocumentedItems.map((item: UndocumentedItem) => (
                  <Box key={item.id} className={styles.scanItem}>
                    <Box className={styles.itemHeader}>
                      <Typography
                        variant="subtitle2"
                        className={styles.itemTitle}
                      >
                        {item.deviceDescription || 'No description'}
                      </Typography>
                      <Box className={styles.qtyControls}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            decrementQuantity(item.id, item.quantity)
                          }
                          disabled={item.quantity <= 1}
                          className={styles.qtyIcon}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          type="text"
                          inputMode="numeric"
                          value={
                            editingQuantities[item.id] ?? String(item.quantity)
                          }
                          onChange={(e) =>
                            handleQuantityInputChange(item.id, e.target.value)
                          }
                          onBlur={() => handleQuantityInputCommit(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur()
                            }
                          }}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            min: 1,
                          }}
                          className={styles.qtyInput}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            incrementQuantity(item.id, item.quantity)
                          }
                          className={styles.qtyIcon}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className={styles.smallText}
                    >
                      UDI: {item.udi || 'No UDI'}
                    </Typography>
                    {item.catalogNumber && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.smallText}
                      >
                        Catalog: {item.catalogNumber}
                      </Typography>
                    )}
                    {item.deviceId && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.smallText}
                      >
                        Device ID: {item.deviceId}
                      </Typography>
                    )}
                    {item.lotBatch && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.smallText}
                      >
                        Lot/Batch: {item.lotBatch}
                      </Typography>
                    )}
                    {item.serialNumber && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.smallText}
                      >
                        Serial: {item.serialNumber}
                      </Typography>
                    )}
                    {item.expirationDate && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.smallText}
                      >
                        Expires:{' '}
                        {new Date(item.expirationDate).toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {item.companyName} • {item.assetType} •{' '}
                      {item.ownershipType}
                      {item.deviceCount &&
                        ` • Device Count: ${item.deviceCount}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className={styles.timestamp}
                    >
                      Captured: {new Date(item.scannedAt).toLocaleTimeString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box className={styles.bottomNavigation}>
        <Button
          variant="outlined"
          onClick={onBack}
          className={styles.backButton}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNextStep}
          disabled={undocumentedItems.length === 0}
          className={styles.continueButton}
        >
          Continue ({pluralize(undocumentedItems.length, 'item')}, {totalQty}{' '}
          qty)
        </Button>
      </Box>
    </Box>
  )
}

export default ScanStep
